import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, AlertCircle, ChevronLeft, ChevronRight, Globe,
} from 'lucide-react';
import {
  listAdminCharacters,
  type AdminCharacterRow,
  type CharacterSort,
} from '@/shared/services/adminService';
import { NATIONS_PT, formatClass } from './labels';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';

const PAGE_SIZE = 25;

const CHAR_COLS: DataTableColumn<AdminCharacterRow>[] = [
  {
    key: 'name', label: 'Personagem', width: '2fr', sortKey: 'name',
    cell: (c) => (
      <div className="min-w-0">
        <p className="text-sm font-bold text-white truncate">{c.name || '—'}</p>
        <p className="text-[10px] text-white/35 font-mono">CharIdx {c.characterIdx}</p>
      </div>
    ),
  },
  {
    key: 'charClass', label: 'Classe', width: '1.4fr', sortKey: 'char_class',
    cell: (c) => <span className="text-xs text-white/75 truncate">{formatClass(c.charClass)}</span>,
  },
  {
    key: 'lev', label: 'Lv.', width: '60px', sortKey: 'lev',
    cell: (c) => <span className="text-sm font-bold text-white">{c.lev ?? '—'}</span>,
  },
  {
    key: 'nation', label: 'Nação', width: '110px', sortKey: 'nation',
    cell: (c) => {
      const nation = c.nation ? NATIONS_PT[c.nation] : null;
      return nation ? (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border ${nation.classes}`}>
          <Globe size={10} /> {nation.name}
        </span>
      ) : <span className="text-[10px] text-white/35">—</span>;
    },
  },
  {
    key: 'alz', label: 'Alz', width: '1fr', sortKey: 'alz',
    cell: (c) => <span className="text-xs font-mono text-white/65">{(c.alz ?? 0).toLocaleString('pt-BR')}</span>,
  },
  {
    key: 'userNum', label: 'Conta', width: '1.2fr', sortKey: 'user_num',
    cell: (c) => (
      <Link
        to={`/admin/account/${c.userNum}`}
        onClick={(e) => e.stopPropagation()}
        className="text-xs text-[#00e1d7]/85 truncate font-semibold hover:underline hover:text-[#00e1d7]"
      >
        {c.accountId ?? `UserNum ${c.userNum}`}
      </Link>
    ),
  },
  {
    key: 'login', label: 'Status', width: '70px', sortKey: 'login',
    cell: (c) => c.login === 1 ? (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#00e1d7]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#00e1d7] shadow-[0_0_6px_#00e1d7]" />
        online
      </span>
    ) : <span className="text-[10px] text-white/35">offline</span>,
  },
];

export default function CharactersPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<CharacterSort>('lev');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [items, setItems] = useState<AdminCharacterRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch, sort, order]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAdminCharacters({
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        sort,
        order,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar personagens');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, sort, order]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  const toggleSort = (key: CharacterSort) => {
    if (sort === key) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setOrder('desc'); }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Personagens</h1>
        <p className="text-sm text-white/65 mt-1">
          Buscar e gerenciar personagens · {total.toLocaleString('pt-BR')} no total
        </p>
      </div>

      <div className="mb-5 relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome do char/conta, CharIdx, UserNum ou data (30/06/2026)..."
          className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg pl-11 pr-4 py-2.5
                     text-sm text-white placeholder:text-white/40
                     focus:outline-none focus:border-[#00e1d7]/40 transition"
        />
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<AdminCharacterRow>
        columns={CHAR_COLS}
        rows={items}
        rowKey={(c) => c.characterIdx}
        rowHref={(c) => `/admin/character/${c.characterIdx}`}
        loading={loading}
        empty="Nenhum personagem encontrado"
        sort={{ key: sort, order }}
        onSort={(key) => toggleSort(key as CharacterSort)}
        mobileTitle={(c) => (
          <span className="flex items-center gap-2">
            {c.name || '—'}
            <span className="text-[10px] text-white/45 font-mono">CharIdx {c.characterIdx}</span>
          </span>
        )}
        mobileSubtitle={(c) => `${formatClass(c.charClass)} · Lv ${c.lev ?? '—'}`}
      />

      <div className="flex items-center justify-between mt-4 text-xs text-white/55">
        <span>Página {page + 1} de {totalPages}</span>
        <div className="flex gap-2">
          <button
            onClick={() => canPrev && setPage((p) => p - 1)}
            disabled={!canPrev || loading}
            className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                       hover:border-[#00e1d7]/40 disabled:opacity-40 disabled:cursor-not-allowed
                       transition cursor-pointer flex items-center gap-1"
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <button
            onClick={() => canNext && setPage((p) => p + 1)}
            disabled={!canNext || loading}
            className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                       hover:border-[#00e1d7]/40 disabled:opacity-40 disabled:cursor-not-allowed
                       transition cursor-pointer flex items-center gap-1"
          >
            Próxima <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
